/**
 * CampaignNewDayPickerPure
 *
 * Trailing inter-day "add a step" affordance. Replaces the legacy
 * `CGAddStepPure` button used between day cards. Mirrors the in-group flow:
 *
 *   button → CampaignStepPickerPure → CTSContentEditingPure
 *
 * The visual chrome matches `CampaignDayCardPure` (rounded border + divider)
 * so a new day in progress reads as a real card under construction, not a
 * floating popover. The day's date is determined by the host (it picks the
 * delay), so this component never renders a date header — only when the user
 * has actually committed (saved) does a real `CampaignDayCardPure` replace it.
 */
import type { ReactNode } from 'react';
import type { CadenceStepTypePure } from '../CGStep/types';
import { cn } from '../cn';
import CTSContentEditingPure, {
  type SiftConfig,
  type StepData,
  type StepType,
} from '../CGStep/CTSContentEditingPure';
import type { ReminderAction } from '../ManualSelect/ManualSelectPure';
import type { TipTapDocument, TipTapVariable } from '../Input2/TipTapInput';
import Button from '../Input2/Button';
import { General } from '../StrokeIcons';
import { CampaignStepPickerPure } from './CampaignStepPickerPure';

// Same gating set as CampaignDayCardPure — types that get the inline editor.
const INLINE_EDITABLE_TYPES_LIST = [
  'visit',
  'connect',
  'message',
  'inmail',
  'email',
  'likeRecentPosts',
  'follow',
  'reminder',
  'sift',
  'voice_note',
] as const satisfies ReadonlyArray<StepType>;

type InlineEditableType = (typeof INLINE_EDITABLE_TYPES_LIST)[number];

const INLINE_EDITABLE_TYPES: ReadonlySet<string> = new Set(INLINE_EDITABLE_TYPES_LIST);

function isInlineEditable(t: CadenceStepTypePure): t is InlineEditableType {
  return INLINE_EDITABLE_TYPES.has(t);
}

export type CampaignNewDayPickerPureProps = {
  /**
   * Default wait length in whole days for the new day. Surfaced in the
   * card header ("After waiting 2 days") so the user can see the delay
   * the new day will land on before committing. Required so the picker
   * card never blends into the surrounding saved cards with no temporal
   * cue.
   */
  defaultDelayDays: number;
  /**
   * Whether there's a saved card directly ABOVE this picker. When true,
   * a short vertical spine is rendered at the top to bridge to it.
   * When false (e.g. picker is at the top of the cadence) the spine is
   * suppressed so the chrome doesn't dangle above the page.
   */
  hasNeighborAbove?: boolean;
  /** Same idea for a card BELOW this picker (suppress at the end of the cadence). */
  hasNeighborBelow?: boolean;
  /** True when the picker (icon row) is shown. Parent owns this state. */
  isPickingStepType: boolean;
  /**
   * When set, renders the inline editor for adding this type. Used for
   * content types — action-only types (follow / likeRecentPosts) are created
   * immediately by the host without passing through this prop.
   */
  addingStepType: CadenceStepTypePure | undefined;
  /**
   * Per-type disabled reasons forwarded to the picker (e.g. `connect` is
   * already part of the cadence, or `sift` not allowed at the start).
   */
  disabledStepReasons: Partial<Record<CadenceStepTypePure, string>> | undefined;
  /**
   * Step types that already exist somewhere upstream — used by the picker
   * to disable once-only types. Pass an empty array if no constraint.
   */
  existingTypes: readonly CadenceStepTypePure[];
  variables: TipTapVariable[] | undefined;
  documents: TipTapDocument[] | undefined;
  /**
   * Subject of the email the new day's step would continue, or null when there's no thread to
   * join (hides the add editor's Thread toggle). See `CGStep/previousEmailStep`.
   */
  previousEmailSubject?: string | null;

  /** Click on the trigger button — host should set isPickingStepType=true. */
  onAddStep: (() => void) | undefined;
  /** User picked a type from the icon row. */
  onPickStepType: ((type: CadenceStepTypePure) => void) | undefined;
  /** User dismissed the picker without choosing a type. */
  onCancelPickStepType: (() => void) | undefined;
  /** User clicked Add inside the inline editor. */
  onSaveNewStep: ((type: CadenceStepTypePure, data: StepData) => void | Promise<void>) | undefined;
  /** User clicked Cancel inside the inline editor. */
  onCancelAddStep: (() => void) | undefined;

  /** Render prop for the sift condition builder (same contract as the card). */
  renderSiftConditionBuilder?: (props: {
    value: SiftConfig;
    onChange: (value: SiftConfig) => void;
  }) => ReactNode;
  reminderActions?: ReminderAction[];
  reminderActionsLoading?: boolean;
  onCreateReminder?: (name: string) => Promise<void>;
  reminderCreating?: boolean;
  onReminderError?: (message: string) => void;

  className?: string;
  /**
   * Forwarded to `CampaignStepPickerPure`. Use `'sm'` in narrow contexts
   * (e.g. the wait-for-connect No-branch pink well) so the option
   * buttons fit without wrapping.
   */
  pickerOptionSize?: 'sm' | 'md';
  /** When true, voice_note step type is available in the picker. */
  voiceEnabled?: boolean;
  /** When true, the call step type is available (team voice-calling flag). */
  voiceCallingEnabled?: boolean;
};

export function CampaignNewDayPickerPure({
  defaultDelayDays,
  hasNeighborAbove = true,
  hasNeighborBelow = true,
  isPickingStepType,
  addingStepType,
  disabledStepReasons,
  existingTypes,
  variables,
  documents,
  previousEmailSubject,
  onAddStep,
  onPickStepType,
  onCancelPickStepType,
  onSaveNewStep,
  onCancelAddStep,
  renderSiftConditionBuilder,
  reminderActions,
  reminderActionsLoading,
  onCreateReminder,
  reminderCreating,
  onReminderError,
  className,
  pickerOptionSize,
  voiceEnabled,
  voiceCallingEnabled,
}: CampaignNewDayPickerPureProps) {
  // Idle state: a single full-width trigger button. Visually distinct from
  // the in-group "add step" (dashed top-border) so the user can tell at a
  // glance that this is "add a NEW DAY", not "add to this day".
  if (!isPickingStepType && addingStepType == null) {
    return (
      <div className={cn('my-3 w-full', className)}>
        <Button
          data-testid="campaign-new-day-trigger"
          text
          size="sm"
          textAlign="center"
          onClick={onAddStep}
          className={cn(
            'w-full justify-center rounded-md border border-dashed border-lynch-300 bg-transparent py-2 text-lynch-700',
            'hover:border-solid hover:border-blue-500 hover:bg-blue-50 hover:text-blue-700',
            'dark:border-slate-700 dark:text-lynch-300 dark:hover:border-blue-400 dark:hover:bg-blue-950 dark:hover:text-blue-300',
          )}>
          <span className="inline-flex items-center gap-1">
            <General.Plus className="size-3.5" />
            add step on a new day
          </span>
        </Button>
      </div>
    );
  }

  // Active states: neutral dashed-border on a slightly tinted bg signals
  // "in progress / unsaved". The wait info renders OUTSIDE the card as a
  // small caption above it — keeps the card chrome focused on the picker
  // / editor without a redundant header strip.
  const dayLabel = defaultDelayDays === 1 ? 'day' : 'days';
  return (
    <div className={cn('flex w-full flex-col items-stretch', className)}>
      {/* Spine segment connecting the previous card's bottom to this card.
          Suppressed when the picker is at the top of the cadence so the
          chrome doesn't dangle into empty space above. */}
      {hasNeighborAbove && <Spine />}
      {defaultDelayDays > 0 && (
        <>
          {/* "AFTER WAITING N DAYS" caption — rendered as a centered
              dashed pill so it visually echoes the dashed-bordered
              draft card below it. */}
          <span
            data-testid="campaign-new-day-delay"
            className={cn(
              'mx-auto inline-flex items-center rounded-full border border-dashed px-2.5 py-0.5',
              'text-[10px] font-medium uppercase tracking-wide',
              'border-lynch-300 text-lynch-600 dark:border-slate-600 dark:text-lynch-400',
            )}>
            after waiting {defaultDelayDays} {dayLabel}
          </span>
          {/* Continue the spine from below the caption down to the card. */}
          <Spine />
        </>
      )}
      <div
        data-testid="campaign-new-day-card"
        className={cn(
          'flex w-full flex-col overflow-hidden rounded-lg border-2 border-dashed shadow-xs',
          'border-lynch-300 bg-lynch-50/60 dark:border-slate-600 dark:bg-slate-800/40',
        )}>

      {addingStepType != null && isInlineEditable(addingStepType) && (
        <div
          className="px-3 py-2"
          data-testid="campaign-new-day-add-editor"
          data-adding-step-type={addingStepType}>
          <CTSContentEditingPure
            stepType={addingStepType}
            stepData={{}}
            previousEmailSubject={previousEmailSubject ?? null}
            // Per-contact: the thread subject can change over time — never overwrite the step's own.
            preserveOwnSubject
            canPersonalize={false}
            variables={variables}
            documents={documents}
            renderSiftConditionBuilder={renderSiftConditionBuilder}
            reminderActions={reminderActions}
            reminderActionsLoading={reminderActionsLoading}
            onCreateReminder={onCreateReminder}
            reminderCreating={reminderCreating}
            onReminderError={onReminderError}
            submitLabel="Add"
            onSubmit={(data) => onSaveNewStep?.(addingStepType, data)}
            onCancel={() => onCancelAddStep?.()}
          />
        </div>
      )}

      {addingStepType == null && isPickingStepType && (
        <CampaignStepPickerPure
          existingTypes={existingTypes}
          disabledTypeReasons={disabledStepReasons}
          optionSize={pickerOptionSize}
          voiceEnabled={voiceEnabled}
          voiceCallingEnabled={voiceCallingEnabled}
          onPickType={(type) => onPickStepType?.(type)}
          onCancel={onCancelPickStepType}
        />
      )}
      </div>
      {/* Spine segment connecting this card's bottom to whatever's below
          (the wait pill leading into the next saved card). Suppressed
          when the picker is the LAST element in the cadence so the
          chrome doesn't dangle below it. */}
      {hasNeighborBelow && <Spine />}
    </div>
  );
}

function Spine() {
  return (
    <span
      aria-hidden
      data-testid="campaign-new-day-spine"
      className="mx-auto block h-3 w-px bg-lynch-200 dark:bg-slate-700"
    />
  );
}
